/**
 * useDateFormatter.js
 *
 * Centralized date formatting and API conversion composable.
 * Part of the **Typed Data Layer** architecture for timezone-safe date handling.
 *
 * ============================================================================
 * TYPED DATA LAYER ARCHITECTURE
 * ============================================================================
 *
 * This composable is the CONVERSION BOUNDARY between API (strings) and
 * components (Date objects). All conversions happen HERE, not in components
 * or HTTP services.
 *
 * ```
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                       COMPONENT LAYER                                    │
 * │  Works with TYPED data (Date objects):                                  │
 * │  • Sorting: Date.getTime() comparison ✓                                 │
 * │  • Filtering: DatePicker (Date) vs data (Date) ✓                        │
 * │  • Display: formatDateTime(Date) → "15/01/2025 12:00" ✓                 │
 * └─────────────────────────────────────────────────────────────────────────┘
 *                                     │
 *                                     ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │              THIS COMPOSABLE (Conversion Boundary)                       │
 * │  INCOMING: fromApiDateTimeFormat("...Z") → Date object                  │
 * │  OUTGOING: toApiDateTimeFormat(Date) → "...Z"                           │
 * │  DISPLAY:  formatDateTime(Date) → localized string                      │
 * └─────────────────────────────────────────────────────────────────────────┘
 *                                     │
 *                                     ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                          HTTP SERVICE (Pure Transport)                   │
 * │  No date handling - just sends/receives JSON                            │
 * │  { created_at: "2025-01-15T15:00:00Z" }                                 │
 * └─────────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * ============================================================================
 * FUNCTION CATEGORIES
 * ============================================================================
 *
 * 1. DATETIME CONVERSION FUNCTIONS (use UTC conversion - for datetime fields):
 *    - toApiDateTimeFormat: Date → "YYYY-MM-DDTHH:mm:ssZ" (ISO 8601 UTC) for API
 *    - fromApiDateTimeFormat: "YYYY-MM-DDTHH:mm:ssZ" → Date for components
 *
 * 2. DATE-ONLY FUNCTIONS (NO timezone conversion - for calendar dates):
 *    - fromDateToApiDate: Date → "YYYY-MM-DD" (Component → API, no timezone shift)
 *    - fromApiDateToDate: "YYYY-MM-DD" → Date at noon local (API → Component)
 *
 * 3. DISPLAY FUNCTIONS (use in components for UI):
 *    - formatDate: Date → localized date string (e.g., "15/01/2025")
 *    - formatDateTime: Date → localized datetime string (e.g., "15/01/2025 10:30:00")
 *    - formatDateLong: Date → long format (e.g., "15 de janeiro de 2025")
 *
 * 4. LEGACY FUNCTIONS (for backward compatibility):
 *    - parseBackendDate: "YYYY-MM-DD" → Date at midnight local
 *    - parseBackendDateTime: "YYYY-MM-DD HH:mm:ss" → Date (no timezone conversion)
 *    - fromDateToApiDateTime: Date → "YYYY-MM-DD HH:mm:ss" (local, no timezone conversion)
 *
 *
 * ============================================================================
 * CONVERSION RESPONSIBILITIES
 * ============================================================================
 *
 * | Layer       | Direction | Conversion                        | Function                |
 * |-------------|-----------|-----------------------------------|-------------------------|
 * | Composable  | INCOMING  | ISO 8601 string → Date object     | fromApiDateTimeFormat() |
 * | Composable  | OUTGOING  | Date object → ISO 8601 string     | toApiDateTimeFormat()   |
 * | Component   | DISPLAY   | Date object → formatted string    | formatDateTime()        |
 *
 * ============================================================================
 * DOMAIN COMPOSABLE PATTERN
 * ============================================================================
 *
 * ```javascript
 * // In src/composables/use{Domain}.js
 * export function useDomain() {
 *   const { fromApiDateTimeFormat, toApiDateTimeFormat } = useDateFormatter();
 *
 *   // INCOMING: Convert API response to typed data
 *   const fetchEntity = async (id) => {
 *     const response = await httpService.get(`/endpoint/${id}`);
 *     const data = response.dados;
 *     return {
 *       ...data,
 *       created_at: fromApiDateTimeFormat(data.created_at),
 *       updated_at: fromApiDateTimeFormat(data.updated_at),
 *     };
 *   };
 *
 *   // OUTGOING: Convert typed data to API format
 *   const saveEntity = async (entity) => {
 *     const payload = {
 *       ...entity,
 *       created_at: toApiDateTimeFormat(entity.created_at),
 *       updated_at: toApiDateTimeFormat(entity.updated_at),
 *     };
 *     return httpService.post('/endpoint', payload);
 *   };
 *
 *   return { fetchEntity, saveEntity };
 * }
 * ```
 *
 * ============================================================================
 * API CONTRACT
 * ============================================================================
 *
 * Frontend ↔ Backend: ISO 8601 UTC format
 *   - Date-only: YYYY-MM-DD (e.g., "2025-01-15")
 *   - DateTime: YYYY-MM-DDTHH:mm:ssZ (e.g., "2025-01-15T15:00:00Z")
 *
 * Why ISO 8601 UTC:
 * - Industry standard for REST APIs
 * - Explicit UTC marker (Z suffix) eliminates timezone ambiguity
 * - Native JavaScript parsing with new Date(isoString)
 * - Backend stores in GMT-3 for legacy compatibility
 * - Users in different timezones see correct local times
 *
 * Example Flow:
 * ```
 * User in browser (GMT-5) selects: 2025-01-15 10:00
 * → toApiDateTimeFormat() → "2025-01-15T15:00:00Z" (ISO 8601 UTC)
 * → API stores as: "2025-01-15 12:00:00" (GMT-3, MySQL format)
 * → API returns: "2025-01-15T15:00:00Z" (ISO 8601 UTC)
 * → fromApiDateTimeFormat() → Date object showing 10:00 in user's browser
 * ```
 *
 * ============================================================================
 * DATATABLE COMPATIBILITY
 * ============================================================================
 *
 * When composables return Date objects, DataTable sorting and filtering
 * work correctly because:
 * - Date comparisons use Date.getTime() (numeric comparison)
 * - DatePicker filter returns Date objects that match the data type
 * - No type mismatch between filter values and row data
 *
 * Reference: See Serena memory `timezone-typed-data-layer` for full details.
 */

import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

/**
 * Pad a number with leading zero if needed
 * @param {number} num - Number to pad
 * @returns {string} - Zero-padded string
 */
function pad(num: number): string {
  return String(num).padStart(2, '0')
}

/**
 * Conversões PURAS de data — sem i18n, sem componente ativo.
 *
 * Estavam dentro de `useDateFormatter()`, que chama `useI18n()`. Isso obrigava
 * qualquer consumidor a existir dentro de um `setup()` — e `useCompromisso()` é
 * instanciado DEPOIS de um `await import()`, quando o Vue já perdeu o
 * componente ativo. O resultado era `MUST_BE_CALL_SETUP_TOP` (código 26 do
 * vue-i18n), que a tela exibia como "Erro ao carregar dados: 26" — chamado
 * 2026-0009. A conversão não precisa de idioma nenhum; só a EXIBIÇÃO precisa.
 *
 * `useDateFormatter()` continua devolvendo estas mesmas funções: nada muda para
 * quem já as consome por lá.
 */

/**
 * Parse a backend datetime string (YYYY-MM-DD HH:mm:ss) to a Date object.
 *
 * Note: This creates a Date in the browser's local timezone.
 * For timezone-aware parsing from API, use fromApiDateTimeFormat() instead.
 *
 * @param {string} datetimeString - DateTime string in YYYY-MM-DD HH:mm:ss format
 * @returns {Date|null} - Date object or null if invalid
 *
 * @example
 * const date = parseBackendDateTime('2025-01-15 14:30:00') // Date object at 14:30 local time
 */
function parseBackendDateTime(datetimeString: string | null): Date | null {
  if (!datetimeString || typeof datetimeString !== 'string') return null

  try {
    // Handle YYYY-MM-DD HH:mm:ss format
    const match = datetimeString.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/)
    if (!match) return null

    const [, year, month, day, hour = '00', minute = '00', second = '00'] = match
    const date = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    )

    // Validate the date
    if (isNaN(date.getTime())) return null

    return date
  } catch (e) {
    console.error('useDateFormatter: Error parsing datetime:', datetimeString, e)
    return null
  }
}


/**
 * Convert a browser Date object to ISO 8601 UTC datetime string for API.
 *
 * This function:
 * 1. Takes a Date object in the browser's local timezone
 * 2. Converts to UTC
 * 3. Returns an ISO 8601 format string (YYYY-MM-DDTHH:mm:ssZ)
 *
 * Use this before sending datetime fields to the API.
 *
 * @param {Date|string} dateTime - Date object or parseable datetime string in browser timezone
 * @returns {string} - DateTime in ISO 8601 format (YYYY-MM-DDTHH:mm:ssZ), or empty string if invalid
 *
 * @example
 * // Browser in GMT-3 (Sao Paulo)
 * const localDateTime = new Date(2025, 0, 15, 10, 30, 0) // Jan 15, 2025, 10:30 local
 * const apiDateTime = toApiDateTimeFormat(localDateTime) // "2025-01-15T13:30:00Z" (UTC)
 *
 * @example
 * // Browser in GMT-5 (New York EST)
 * const localDateTime = new Date(2025, 0, 15, 19, 0, 0) // Jan 15, 2025, 19:00 local
 * const apiDateTime = toApiDateTimeFormat(localDateTime) // "2025-01-16T00:00:00Z" (UTC, next day)
 */
function toApiDateTimeFormat(dateTime: any) {
  let dateObj = dateTime

  if (typeof dateTime === 'string') {
    dateObj = new Date(dateTime)
  }

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    console.warn('useDateFormatter: Invalid datetime provided to toApiDateTimeFormat', dateTime)
    return ''
  }

  try {
    // Use native toISOString() which returns ISO 8601 UTC format
    // e.g., "2025-01-15T13:30:00.000Z"
    // We truncate milliseconds for cleaner format: "2025-01-15T13:30:00Z"
    const isoString = dateObj.toISOString()
    return isoString.replace(/\.\d{3}Z$/, 'Z')
  } catch (e) {
    console.error('useDateFormatter: Error in toApiDateTimeFormat:', dateTime, e)
    return ''
  }
}


/**
 * Browser-local datetime (what InputDateTime emits, "YYYY-MM-DD HH:mm:ss",
 * or a Date) → ISO 8601 UTC for the API. Already-ISO input passes through.
 *
 * This is the App → API boundary for every form that holds a naive local
 * string: without it the backend stores the browser's wall-clock as GMT-3
 * and a browser in Spain (UTC+2) sees every hour shifted by +5.
 */
export function localDateTimeToApi(value: Date | string | null | undefined): string {
  if (!value) return ''
  if (value instanceof Date) return toApiDateTimeFormat(value)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)) {
    return value.replace(/\.\d{3}Z$/, 'Z')
  }
  const local = parseBackendDateTime(value)
  return local ? toApiDateTimeFormat(local) : ''
}


export function useDateFormatter() {
  const { d, locale } = useI18n()

  // ============================================================================
  // DISPLAY FUNCTIONS - Parse and format for UI display
  // ============================================================================

  /**
   * Parse a backend date string (YYYY-MM-DD) to a Date object.
   *
   * Note: This creates a Date in the browser's local timezone at midnight.
   * For timezone-aware parsing from API, use fromApiDateFormat() instead.
   *
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @returns {Date|null} - Date object or null if invalid
   *
   * @example
   * const date = parseBackendDate('2025-01-15') // Date object at midnight local time
   */
  function parseBackendDate(dateString: string | null): Date | null {
    if (!dateString || typeof dateString !== 'string') return null

    try {
      // Handle YYYY-MM-DD format
      const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (!match) return null

      const [, year, month, day] = match
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))

      // Validate the date
      if (isNaN(date.getTime())) return null

      return date
    } catch (e) {
      console.error('useDateFormatter: Error parsing date:', dateString, e)
      return null
    }
  }

  /**
   * Parse a date-only string (YYYY-MM-DD) to a Date object at noon local time.
   *
   * TYPED DATA LAYER: Use this for date-only fields (no timezone context).
   * Creates the date at noon (12:00) local time to avoid timezone edge cases.
   *
   * This is different from:
   * - parseBackendDate(): Creates at midnight local (can have edge cases)
   * - fromApiDateFormat(): Creates at midnight UTC (wrong for date-only fields)
   *
   * Use cases:
   * - Filter dates (DataInicio, DataFim)
   * - Birth dates
   * - Any date field that doesn't have a time component
   *
   * @param {string} dateString - Date string in YYYY-MM-DD format
   * @returns {Date|null} - Date object at noon local time or null if invalid
   *
   * @example
   * const date = fromApiDateToDate('2025-12-01') // Date object at 12:00 local time
   * formatDate(date) // "01/12/2025" (correct regardless of timezone)
   */
  function fromApiDateToDate(dateString: string | null): Date | null {
    if (!dateString || typeof dateString !== 'string') return null

    try {
      const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (!match) return null

      const [, year, month, day] = match
      // Create at noon local time to avoid timezone edge cases
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0)

      if (isNaN(date.getTime())) return null

      return date
    } catch (e) {
      console.error('useDateFormatter: Error in fromApiDateToDate:', dateString, e)
      return null
    }
  }


  /**
   * Format a Date object for display using i18n short format.
   *
   * Uses the current locale to format the date appropriately:
   * - pt-BR: "15/01/2025"
   * - en-US: "01/15/2025"
   *
   * @param {Date} date - Date object to format
   * @returns {string} - Formatted date string
   *
   * @example
   * const display = formatDate(new Date(2025, 0, 15)) // "15/01/2025" (pt-BR)
   */
  function formatDate(date: Date | null): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''

    try {
      return d(date, 'short')
    } catch (e) {
      console.error('useDateFormatter: Error formatting date:', date, e)
      return ''
    }
  }

  /**
   * Parse a locale-formatted display date back to a Date.
   *
   * Inverse of formatDate. Uses Intl.DateTimeFormat.formatToParts to discover
   * the day/month/year order for the current locale (en-US → MM/DD/YYYY,
   * pt-BR → DD/MM/YYYY, es-CL → DD-MM-YYYY) instead of hardcoding day-first.
   *
   * @returns Date or null on bad input.
   */
  function parseDisplayDate(s: string | null | undefined): Date | null {
    if (typeof s !== 'string') return null
    const numbers = s.trim().match(/\d+/g)
    if (!numbers || numbers.length !== 3) return null

    const ref = new Date(2025, 0, 15) // month=Jan, day=15 — distinguishable
    let dayIdx = 0
    let monthIdx = 1
    let yearIdx = 2
    try {
      const parts = new Intl.DateTimeFormat(locale.value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).formatToParts(ref)
      const order = parts.filter((p) => p.type === 'day' || p.type === 'month' || p.type === 'year')
      const idx = (type: string): number => order.findIndex((p) => p.type === type)
      dayIdx = idx('day')
      monthIdx = idx('month')
      yearIdx = idx('year')
      if (dayIdx < 0 || monthIdx < 0 || yearIdx < 0) {
        dayIdx = 0; monthIdx = 1; yearIdx = 2
      }
    } catch {
      // Fall through to defaults.
    }

    const day = Number(numbers[dayIdx])
    const month = Number(numbers[monthIdx])
    const year = Number(numbers[yearIdx])
    if (!day || !month || !year || month < 1 || month > 12 || day < 1 || day > 31) {
      return null
    }
    const date = new Date(year, month - 1, day)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null
    }
    return date
  }

  /**
   * Format a Date object for display using i18n long format.
   *
   * @param {Date} date - Date object to format
   * @returns {string} - Formatted date string (e.g., "15 de janeiro de 2025")
   *
   * @example
   * const display = formatDateLong(new Date(2025, 0, 15)) // "15 de janeiro de 2025"
   */
  function formatDateLong(date: Date | null): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''

    try {
      return d(date, 'long')
    } catch (e) {
      console.error('useDateFormatter: Error formatting date (long):', date, e)
      return ''
    }
  }

  /**
   * Format a Date object for display using i18n datetime format.
   *
   * @param {Date} date - Date object to format
   * @returns {string} - Formatted datetime string (e.g., "15/01/2025 14:30:00")
   *
   * @example
   * const display = formatDateTime(new Date(2025, 0, 15, 14, 30)) // "15/01/2025 14:30:00"
   */
  function formatDateTime(date: Date | null): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''

    try {
      return d(date, 'datetime')
    } catch (e) {
      console.error('useDateFormatter: Error formatting datetime:', date, e)
      return ''
    }
  }

  /**
   * Format a Date object to MySQL date format (YYYY-MM-DD) without timezone conversion.
   *
   * Note: This uses the browser's local date. For UTC conversion for API,
   * use toApiDateFormat() instead.
   *
   * @param {Date} date - Date object to format
   * @returns {string} - Date string in YYYY-MM-DD format
   *
   * @example
   * const apiDate = fromDateToApiDate(new Date(2025, 0, 15)) // "2025-01-15"
   */
  function fromDateToApiDate(date: Date | null): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''

    try {
      const year = date.getFullYear()
      const month = pad(date.getMonth() + 1)
      const day = pad(date.getDate())
      return `${year}-${month}-${day}`
    } catch (e) {
      console.error('useDateFormatter: Error in fromDateToApiDate:', date, e)
      return ''
    }
  }

  /**
   * Format a Date object to MySQL datetime format (YYYY-MM-DD HH:mm:ss) without timezone conversion.
   *
   * Note: This uses the browser's local time. For UTC conversion for API,
   * use toApiDateTimeFormat() instead.
   *
   * @param {Date} date - Date object to format
   * @returns {string} - Datetime string in YYYY-MM-DD HH:mm:ss format
   *
   * @example
   * const backend = fromDateToApiDateTime(new Date(2025, 0, 15, 14, 30, 0)) // "2025-01-15 14:30:00"
   */
  function fromDateToApiDateTime(date: Date | null): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''

    try {
      const year = date.getFullYear()
      const month = pad(date.getMonth() + 1)
      const day = pad(date.getDate())
      const hour = pad(date.getHours())
      const minute = pad(date.getMinutes())
      const second = pad(date.getSeconds())
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    } catch (e) {
      console.error('useDateFormatter: Error formatting backend datetime:', date, e)
      return ''
    }
  }

  // ============================================================================
  // API CONVERSION FUNCTIONS - Timezone-aware for API communication
  // ============================================================================


  /**
   * Parse an ISO 8601 UTC datetime string from API to browser Date object.
   *
   * This function:
   * 1. Takes an ISO 8601 format string (YYYY-MM-DDTHH:mm:ssZ) assumed to be UTC
   * 2. Creates a Date object at that exact UTC time
   * 3. Returns the Date which will display in browser's local timezone
   *
   * Use this after receiving datetime fields from the API.
   *
   * @param {string} dateTimeString - DateTime string in ISO 8601 format (YYYY-MM-DDTHH:mm:ssZ)
   * @returns {Date|null} - Date object in browser's local timezone, or null if invalid
   *
   * @example
   * // API returns ISO 8601 UTC datetime
   * const apiDateTime = "2025-01-15T13:30:00Z"
   * const localDateTime = fromApiDateTimeFormat(apiDateTime)
   *
   * // In browser GMT-3: Date object representing Jan 15, 2025 10:30 local
   * // 13:30 UTC = 10:30 in GMT-3
   * formatDateTime(localDateTime) // "15/01/2025 10:30:00"
   *
   * @example
   * // In browser GMT-5 (New York EST)
   * const apiDateTime = "2025-01-16T00:00:00Z"
   * const localDateTime = fromApiDateTimeFormat(apiDateTime)
   * // 00:00 UTC Jan 16 = 19:00 Jan 15 in GMT-5
   * formatDateTime(localDateTime) // "01/15/2025 19:00:00" (en-US)
   */
  function fromApiDateTimeFormat(dateTimeString: string | null): Date | null {
    if (!dateTimeString || typeof dateTimeString !== 'string') return null

    try {
      // Native JavaScript can parse ISO 8601 format directly
      // Expected format: "2025-01-15T13:30:00Z"
      const date = new Date(dateTimeString)

      // Validate
      if (isNaN(date.getTime())) {
        console.warn('useDateFormatter: Invalid ISO 8601 format:', dateTimeString)
        return null
      }

      return date
    } catch (e) {
      console.error('useDateFormatter: Error in fromApiDateTimeFormat:', dateTimeString, e)
      return null
    }
  }

  // ============================================================================
  // LOCALE CONFIGURATION - For PrimeVue DatePicker components
  // ============================================================================

  /**
   * Get the current locale's date format string for PrimeVue DatePicker.
   *
   * Maps locale to PrimeVue's date format notation.
   *
   * @returns {string} - PrimeVue date format (e.g., "dd/mm/yy", "mm/dd/yy")
   */
  const dateFormat = computed(() => {
    const localeValue = locale.value

    // Map locale to date format
    // Most locales use dd/mm/yy except US which uses mm/dd/yy
    const formatMap = {
      'en-US': 'mm/dd/yy',
      'pt-BR': 'dd/mm/yy',
      'pt-PT': 'dd/mm/yy',
      'pt-AO': 'dd/mm/yy',
      'en-GB': 'dd/mm/yy',
      'es-ES': 'dd/mm/yy',
      'es-GT': 'dd/mm/yy',
      'fr-FR': 'dd/mm/yy'
    }

    return (formatMap as any)[localeValue] || 'dd/mm/yy'
  })

  /**
   * Get locale-specific date separator (/ or .)
   *
   * @returns {string} - Date separator character
   */
  const dateSeparator = computed(() => {
    const localeValue = locale.value

    // Most locales use /, some European locales use .
    const separatorMap = {
      'en-US': '/',
      'pt-BR': '/',
      'pt-PT': '/',
      'pt-AO': '/',
      'en-GB': '/',
      'es-ES': '/',
      'es-GT': '/',
      'fr-FR': '/'
    }

    return (separatorMap as any)[localeValue] || '/'
  })


  /**
   * ISO 8601 UTC from the API → browser-local "YYYY-MM-DD HH:mm:ss" for
   * InputDateTime (the inverse of localDateTimeToApi).
   */
  function apiDateTimeToLocalInput(value: string | null | undefined): string {
    if (!value) return ''
    const parsed = fromApiDateTimeFormat(value)
    return parsed ? fromDateToApiDateTime(parsed) : ''
  }

  return {
    // Display functions (i18n-aware)
    parseBackendDate,
    parseBackendDateTime,
    localDateTimeToApi,
    apiDateTimeToLocalInput,
    formatDate,
    parseDisplayDate,
    formatDateLong,
    formatDateTime,
    fromDateToApiDateTime,

    // DATE-ONLY API conversion (NO timezone conversion - prevents -1 day bug)
    fromApiDateToDate,     // "YYYY-MM-DD" → Date (API → Component)
    fromDateToApiDate,     // Date → "YYYY-MM-DD" (Component → API)

    // DATETIME API conversion (WITH UTC timezone conversion)
    toApiDateTimeFormat,   // Date → "YYYY-MM-DDTHH:mm:ssZ" (Component → API)
    fromApiDateTimeFormat, // "YYYY-MM-DDTHH:mm:ssZ" → Date (API → Component)

    // Locale configuration
    dateFormat,
    dateSeparator
  }
}
